import { useEffect, useState } from "react";
import { Car, Loader2, DollarSign, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { listAvailableVehicles, placeBid } from "@/Calls/callSalvageAuth";

interface Vehicle {
    vid: number; did: number; vyear: string | null; vmake: string | null;
    vmodel: string | null; vvin: string | null; vmileage: string | null;
    vlivestatus: string | null; vthubmnail_file: string | null;
    existing_bid: { id: number; bid_amount: number; status: string } | null;
}

export default function SalvageVehiclesPage() {
    const [vehicles,  setVehicles]  = useState<Vehicle[]>([]);
    const [loading,   setLoading]   = useState(true);
    const [bidTarget, setBidTarget] = useState<Vehicle | null>(null);
    const [bidAmount, setBidAmount] = useState("");
    const [bidding,   setBidding]   = useState(false);
    const [bidError,  setBidError]  = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        listAvailableVehicles()
            .then((d) => setVehicles(d.vehicles ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const handleBid = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!bidTarget?.vvin) return;
        setBidding(true);
        setBidError(null);
        try {
            await placeBid(bidTarget.vvin, parseFloat(bidAmount), bidTarget.did);
            setBidTarget(null);
            setBidAmount("");
            load();
        } catch (err: any) {
            setBidError(err.response?.data?.message ?? "Bid failed");
        } finally {
            setBidding(false);
        }
    };

    if (loading) return (
        <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Available Vehicles</h1>
                <p className="text-muted-foreground text-sm mt-0.5">{vehicles.length} vehicles from linked dealers</p>
            </div>

            {vehicles.length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground">
                    No vehicles available from your linked dealers
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {vehicles.map((v) => (
                        <Card key={v.vid} className="overflow-hidden">
                            <div className="h-40 bg-slate-100 flex items-center justify-center">
                                {v.vthubmnail_file ? (
                                    <img src={v.vthubmnail_file} alt="vehicle" className="h-full w-full object-cover" />
                                ) : (
                                    <Car className="h-12 w-12 text-muted-foreground/30" />
                                )}
                            </div>
                            <div className="p-4">
                                <p className="font-semibold">
                                    {v.vyear} {v.vmake} {v.vmodel}
                                </p>
                                {v.vvin && <p className="text-xs text-muted-foreground mt-0.5">VIN: {v.vvin}</p>}
                                {v.vmileage && <p className="text-xs text-muted-foreground">{parseInt(v.vmileage).toLocaleString()} mi</p>}

                                {v.existing_bid ? (
                                    <div className="mt-3 p-2.5 rounded-lg bg-muted/50 flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground">Your bid:</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-mono font-medium">
                                                ${Number(v.existing_bid.bid_amount).toLocaleString()}
                                            </span>
                                            <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${
                                                v.existing_bid.status === "accepted" ? "bg-green-100 text-green-700"
                                                : v.existing_bid.status === "rejected" ? "bg-red-100 text-red-700"
                                                : "bg-yellow-100 text-yellow-700"
                                            }`}>
                                                {v.existing_bid.status.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => { setBidTarget(v); setBidAmount(""); setBidError(null); }}
                                        className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors"
                                    >
                                        <DollarSign className="h-3.5 w-3.5" /> Place Bid
                                    </button>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Bid Modal */}
            {bidTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <Card className="w-full max-w-sm p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="font-semibold">Place Bid</h3>
                                <p className="text-sm text-muted-foreground">
                                    {bidTarget.vyear} {bidTarget.vmake} {bidTarget.vmodel}
                                </p>
                            </div>
                            <button onClick={() => setBidTarget(null)} className="text-muted-foreground hover:text-foreground">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <form onSubmit={handleBid} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Bid Amount ($)</label>
                                <input
                                    type="number" min="1" step="1" required
                                    value={bidAmount} onChange={(e) => setBidAmount(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    placeholder="Enter amount"
                                />
                            </div>
                            {bidError && <p className="text-red-500 text-sm">{bidError}</p>}
                            <div className="flex gap-3">
                                <button
                                    type="button" onClick={() => setBidTarget(null)}
                                    className="flex-1 py-2 rounded-lg border text-sm hover:bg-muted transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit" disabled={bidding}
                                    className="flex-1 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                                >
                                    {bidding && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                    Submit Bid
                                </button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}
        </div>
    );
}
